import { ExclamationTriangleIcon } from '@heroicons/react/20/solid';
import React from 'react';

import { Button } from '@/components/ui/catalyst/button';

interface ImportActionBarProps {
  importableTaskCount: number;
  skippedTaskCount: number;
  unmatchedCaseCount: number;
  onImport: () => void;
  isImporting: boolean;
  hasUnsavedScopeChanges?: boolean;
}

export const ImportActionBar: React.FC<ImportActionBarProps> = ({
  importableTaskCount,
  skippedTaskCount,
  unmatchedCaseCount,
  onImport,
  isImporting,
  hasUnsavedScopeChanges = false,
}) => {
  const canImport = importableTaskCount > 0 && !hasUnsavedScopeChanges;

  return (
    <div className="bg-white dark:bg-zinc-900 rounded-lg border border-zinc-200 dark:border-zinc-800 p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {/* Warning if unsaved scope changes */}
          {hasUnsavedScopeChanges && (
            <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
              <ExclamationTriangleIcon className="h-5 w-5" />
              <span className="text-sm">
                Save your import scope changes before importing
              </span>
            </div>
          )}

          {/* Info about skipped tasks due to unmatched cases */}
          {!hasUnsavedScopeChanges && skippedTaskCount > 0 && (
            <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
              <ExclamationTriangleIcon className="h-5 w-5" />
              <span className="text-sm">
                {skippedTaskCount} task{skippedTaskCount !== 1 ? 's' : ''} from{' '}
                {unmatchedCaseCount} unmatched case
                {unmatchedCaseCount !== 1 ? 's' : ''} won&apos;t be imported
              </span>
            </div>
          )}

          {/* No tasks warning */}
          {!hasUnsavedScopeChanges &&
            importableTaskCount === 0 &&
            skippedTaskCount === 0 && (
              <div className="flex items-center gap-2 text-zinc-500 dark:text-zinc-400">
                <span className="text-sm">No tasks selected for import</span>
              </div>
            )}

          {/* Ready to import - all tasks matched */}
          {!hasUnsavedScopeChanges &&
            importableTaskCount > 0 &&
            skippedTaskCount === 0 && (
              <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                <svg
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span className="text-sm">All tasks ready to import</span>
              </div>
            )}
        </div>

        {/* Import Button */}
        <Button
          color="blue"
          onClick={onImport}
          disabled={!canImport || isImporting}
        >
          {isImporting ? (
            <>
              <svg
                className="animate-spin -ml-1 mr-2 h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Importing...
            </>
          ) : (
            `Import ${importableTaskCount} Task${importableTaskCount !== 1 ? 's' : ''}`
          )}
        </Button>
      </div>
    </div>
  );
};
